import { FileUploadImportConfig } from '@oneschema/react';
import React, { useCallback, useState } from 'react';

import { OneSchemaWrapper } from '@neo.tax/shared';

import { RdBusinessComponentsUploadInstructionModal } from '../rd-business-components-upload-instruction-modal';
import { RD_BUSINESS_COMPONENTS_UPLOAD_ONESCHEMA_TEMPLATE_KEY } from './bulk-upload.utils';
import { useAddRdBusinessComponentsFile } from './hooks';

type RdBusinessComponentsUploadModalProps = {
  close: () => void;
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
};

export const RdBusinessComponentsUploadModal = ({
  close,
  companyId,
  fiscalYearId,
  isOpen,
}: RdBusinessComponentsUploadModalProps): React.ReactElement => {
  const [hasLaunched, setHasLaunched] = useState<boolean>(false);
  const [hasConfirmedInstruction, setHasConfirmedInstruction] = useState<boolean>(false);

  const { handleSuccess, oneSchemaJwt, permanentUrl, signedUrl } = useAddRdBusinessComponentsFile({
    companyId,
    fiscalYearId,
    isOpen,
  });

  const resetState = useCallback((): void => {
    close();
    setHasConfirmedInstruction(false);
    setHasLaunched(false);
  }, [close]);

  const onSuccess = useCallback(async (): Promise<void> => {
    try {
      await handleSuccess();
    } finally {
      resetState();
    }
  }, [handleSuccess, resetState]);

  const onLaunched = useCallback((): void => {
    setHasLaunched(true);
  }, []);

  const isOneSchemaOpen = isOpen && !!oneSchemaJwt && !!signedUrl && !!permanentUrl && hasConfirmedInstruction;

  const importConfig: FileUploadImportConfig = {
    format: 'csv',
    type: 'file-upload',
    url: signedUrl ?? '',
  };

  return (
    <>
      {!hasLaunched && (
        <RdBusinessComponentsUploadInstructionModal
          isOpen={isOpen}
          close={resetState}
          loading={hasConfirmedInstruction}
          submit={(): void => setHasConfirmedInstruction(true)}
        />
      )}
      <OneSchemaWrapper
        isOpen={isOneSchemaOpen}
        userJwt={oneSchemaJwt ?? ''}
        templateKey={RD_BUSINESS_COMPONENTS_UPLOAD_ONESCHEMA_TEMPLATE_KEY}
        importConfig={importConfig}
        onCancel={resetState}
        onError={resetState}
        onSuccess={onSuccess}
        onLaunched={onLaunched}
      />
    </>
  );
};
